// Role-based screen access (screen gating only; row-level data isolation is a later pass).
//
// A role maps to either the string '*' (full access) or an explicit list of allowed route paths.
// Unknown / unprovisioned users default to ADMIN in the backend, so they get full access here too.

// Base set shared by the sub-contractor personas (IVY's, LCR):
// Scan, Tickets, Invoice, Payroll, and Masters EXCEPT Carriers & Plants.
const STAFF_BASE = [
  '/ScanTicket',
  // Combined Create Invoice + Settlements entry point. Both originals stay listed below so old
  // links keep working while people move across.
  '/Invoicing',
  '/Ticket',
  '/Verify',
  '/IvyInvoice',
  '/Payroll',
  '/Masters/Drivers',
  '/Masters/Trucks',
  '/Masters/Companies',
  '/Masters/RateCards',
  '/Masters/PlantPaymentConfigs',
  '/Masters/HauledForRules',
];

const ADMIN_ONLY_PATHS = ['/Admin/Users'];

export const ROLE_SCREENS = {
  ADMIN: '*',
  DISPATCHER: '*',
  // IVY's staff and LCR staff share the same screens.
  // Field Tickets + Field Ticket Invoice + Vendors hidden for now (2026-07-08) — add
  // '/FieldTickets', '/FieldTicketInvoice', '/Masters/Vendors' back to re-enable.
  IVY_STAFF: [...STAFF_BASE, '/IvyPlantInvoice', '/FlatbedInvoice', '/FieldTicketInvoice', '/Masters/Plants'],
  // Ivy's flatbed-only users (e.g. Jimmy): access limited to the Flatbed Invoice screen.
  IVY_FLATBED: ['/FlatbedInvoice'],
  LCR_STAFF: STAFF_BASE,
  // LSJ's office (Blake). Their drivers use the public, no-login upload page, so the only screen
  // anyone at LSJ signs in for is the intake queue: confirm what was read, export the week to Excel.
  // Phase 1 stops there deliberately — LSJ bill from their own scripts, so no invoicing, no
  // settlements, no payroll, and no masters they would never populate.
  LSJ_ADMIN: ['/TicketIntake'],
  // Owner-Operator (Meta Roads): Scan, Tickets, Payroll, Masters, PLUS Create Invoice, Weekly Recon (P&L),
  // Ticket Report, Fuel & Expenses and Reconciliation.
  // Disabled for Meta Roadways (2026-08-02): Settlements (/IvyInvoice — dropped from the STAFF_BASE it
  // shares with IVY/LCR), Weekly Summary (/WeeklySummary + /RevenueSummary alias). Plant Invoice
  // (/IvyPlantInvoice), Field Ticket Invoice (/FieldTicketInvoice), Weekly Invoice (/invoices/weekly) and
  // Flatbed Invoice (/FlatbedInvoice) were never granted to this role, so they stay hidden.
  OWNER_OPERATOR: [
    ...STAFF_BASE.filter((p) => p !== '/IvyInvoice'),
    '/CreateInvoice',
    '/WeeklyRecon',
    '/TicketReport',
    '/FuelExpenses',
    '/Reconciliation',
    // '/Masters/Vendors', // hidden for now (2026-07-08)
  ],

  // Self-onboarded customers (2026-11-02): each is the admin of their own tenant and nobody else's.
  // Deliberately NOT '*' — that would show them Plants and Carriers, which are shared across tenants,
  // and the admin-only tools. Billing is theirs to manage.
  DISPATCHER_ADMIN: [...STAFF_BASE, '/Billing'],
  OWNER_OPERATOR_ADMIN: [
    ...STAFF_BASE.filter((p) => p !== '/IvyInvoice'),
    '/CreateInvoice',
    '/WeeklyRecon',
    '/TicketReport',
    '/FuelExpenses',
    '/Billing',
  ],
  // Dry van: trucks, drivers and pay % for now; loads, expenses and the P&L arrive with the dry van module.
  DRY_VAN_ADMIN: ['/Masters/Trucks', '/Masters/Drivers', '/Billing'],
};

const DEFAULT_ROLE = 'ADMIN';

// Plants a role should never see (data-visibility rule, matched case-insensitively against the
// plant name OR code). IVY's works with Colorado / Martin only — never CEMEX.
const HIDDEN_PLANTS_BY_ROLE = {
  IVY_STAFF: ['cemex'],
};

/** Filter a list of plant objects ({ name, code }) down to those the given role may see. */
export function visiblePlants(role, plants) {
  const list = Array.isArray(plants) ? plants : [];
  const hidden = HIDDEN_PLANTS_BY_ROLE[role];
  if (!hidden) return list;
  return list.filter((p) => {
    const hay = `${p?.name || ''} ${p?.code || ''}`.toLowerCase();
    return !hidden.some((h) => hay.includes(h));
  });
}

/**
 * Label for the ticket "FS code" concept. IVY's staff call it the Job Code
 * (and don't have a separate order/job code), so surface it as "Job Code" for them.
 */
export function codeLabel(role) {
  return role === 'IVY_STAFF' ? 'Job Code' : 'FS Code';
}

/** True when the role is a recognized, configured application role. */
export function isConfiguredRole(role) {
  return !!role && Object.prototype.hasOwnProperty.call(ROLE_SCREENS, role);
}

export function allowedPaths(role) {
  const screens = ROLE_SCREENS[role] || ROLE_SCREENS[DEFAULT_ROLE];
  return screens;
}

export function canAccess(role, path) {
  if (ADMIN_ONLY_PATHS.includes(path)) return role === 'ADMIN';
  const screens = allowedPaths(role);
  if (screens === '*') return true;
  return screens.includes(path);
}

/** First path a role is allowed to land on (used to redirect away from blocked routes). */
export function firstAllowedPath(role) {
  const screens = allowedPaths(role);
  if (screens === '*') return '/ScanTicket';
  return screens[0] || '/';
}
